import { useDispatch } from 'react-redux';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Geometry } from '@tcc/common';
import { Location } from '../../../../types/Location';
import {
  getModalMetaData,
  getLocale,
  getHarvestArdDataFeatureSelector
} from '../../../../selectors';
import {
  getCurrentSeason,
  getCurrentSeasonCropCode,
} from '../../../../selectors/seasonSelectors';
import { isAirSeederPopulation } from '../../../../selectors/plantingSelectors';
import { REGION_NOTE } from '../../../../constants/modal';
import {
  requestDeleteActivity as deleteActivity,
  setSelectedActivity as setActivity
} from '../../../../actions/scoutingActions';
import { getInversedFieldBoundary } from '../../../../selectors/fieldSelectors';
import { getFieldUuid } from '../../../../selectors/fieldSelectorsNew';
import {
  closeModal,
  openModal,
  resizeModal,
} from '../../../../actions/modalActions';
import { trackEvent, trackHeapEvent } from '../../../../actions/analyticsActions';
import { useAppSelector } from '../../../../utils';
import {
  ModalMode,
  ModalSize,
  YieldRegionReport
} from '../../../../types';
import { currentFieldSelector } from '../../../../selectors/fieldsSelectors';
import { getGeoJson } from '../../../../utils/geo';
import { stableSha256FromValue } from '../../../../utils/hash';
import { getHarvestState, getLegacySeasonTag } from '../../../../selectors/harvestSelectors';
import { isValidHarvestData } from '../../../../utils/regionUtils';
import { logDmbError } from '../../../../utils/error';

import { 
  useRegionReportQueries,
} from './useReportQuery';
import { getGraphQlErrorMessage } from './services/regionReportQueryService';

const modalStates = {
  list: 'list',
  detail: 'detail',
};

export type UseRegionReportControllerParams = {
  index: number;
};

export const useRegionReportController = ({
  index
}: UseRegionReportControllerParams) => {
  const dispatch = useDispatch();
  const state = useAppSelector((storeState) => storeState);
  const currentSeasonYear = getCurrentSeason(state, { index });
  const harvestData = getHarvestState(state, { index });
  const hasHarvestData = isValidHarvestData(harvestData);
  const { activity, mode: regionFormMode } = getModalMetaData(state);
  const fieldName = currentFieldSelector(state).name;
  const fieldBoundary = getInversedFieldBoundary(state);
  const fieldUUID = getFieldUuid(state);

  const seasonTag = currentSeasonYear;
  const isHarvestArdDataEnabled = useAppSelector(getHarvestArdDataFeatureSelector);
  const frrSeasonTag = isHarvestArdDataEnabled
    ? getLegacySeasonTag(state, { index })
    : seasonTag;

  const cropCode = getCurrentSeasonCropCode(state, { index });
  const isAirSeeder = isAirSeederPopulation(state, { index }) as boolean;
  const locale = getLocale(state);

  const [itemData, setItemData] = useState<YieldRegionReport | null>(null);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [closeConfirmationModal, setCloseConfirmationModal] = useState(false);
  const [child, setChild] = useState(modalStates.list);
  const geometry = (activity?.location || activity?.geometry) as Geometry | Location | undefined;
  const geometryHash = useMemo(() => stableSha256FromValue(geometry ?? {}), [geometry]);
  const isValidationEnabled = Boolean(fieldBoundary?.coordinates && geometry?.coordinates);
  const lastNullResponseTrackingRef = useRef('');
  const lastReportGraphQlErrorRef = useRef<string | undefined>();

  const activityExists = Boolean(activity?.id);

  const {
    isRegionValid,
    reportData,
    isLoadingReport,
    isReportSuccess,
    isReportFetched,
    reportError,
    hasReportError,
    reportDataUpdatedAt,
  } = useRegionReportQueries({
    geometry,
    geometryHash,
    fieldBoundary,
    fieldUUID,
    isValidationEnabled,
    hasHarvestData,
    frrSeasonTag,
    lastReportGraphQlErrorRef,
  });

  useEffect(() => {
    if (isReportSuccess && reportData) {
      dispatch(trackEvent('field_region_report', 'viewed', {
        shape: geometry?.type,
      }));
    }
  }, [reportDataUpdatedAt, isReportSuccess, reportData, geometry?.type, dispatch]);

  const hasRegionReportData =
    reportData && reportData.area && reportData.area.v && isRegionValid;

  useEffect(() => {
    if (isLoadingReport || hasRegionReportData) {
      return;
    }

    // Avoid firing telemetry before validation has resolved.
    if (hasHarvestData && isValidationEnabled && isRegionValid === null) {
      return;
    }

    const hasNullResponseGraphQlError = Boolean(
      isReportSuccess
      && isReportFetched
      && !reportData
      && lastReportGraphQlErrorRef.current
    );

    let reason = 'empty_report';
    if (!hasHarvestData) {
      reason = 'no_harvest_data';
    } else if (isRegionValid === false) {
      reason = 'invalid_region';
    } else if (hasReportError || hasNullResponseGraphQlError) {
      reason = 'query_error';
    }

    if ((reason === 'empty_report' || reason === 'query_error') && !isReportFetched && hasHarvestData) {
      return;
    }

    const errorMessage = reason === 'query_error'
      ? (getGraphQlErrorMessage(reportError)
        || reportError?.message
        || lastReportGraphQlErrorRef.current)
      : undefined;
    const trackingKey = `${fieldUUID}:${frrSeasonTag}:${geometryHash}:${reason}:${errorMessage || ''}`;

    if (lastNullResponseTrackingRef.current === trackingKey) {
      return;
    }

    lastNullResponseTrackingRef.current = trackingKey;

    if (reason === 'no_harvest_data' || reason === 'invalid_region') {
      dispatch(trackEvent('FRR No Data', { reason }));
    }

    const noDataErrorMessage = errorMessage
      ? `[FRR] No Data (${reason}): ${errorMessage}`
      : `[FRR] No Data (${reason})`;

    logDmbError(new Error(noDataErrorMessage), {
      reason,
      requestError: errorMessage,
      fieldUUID,
      seasonTag: frrSeasonTag,
    });
  }, [isLoadingReport, hasRegionReportData, hasHarvestData, isRegionValid, hasReportError, isReportSuccess, isReportFetched, fieldUUID, frrSeasonTag, geometryHash, isValidationEnabled, reportData, reportError, dispatch]);

  const onStep2Click = useCallback(() => {
    setItemData(null);
    setChild(modalStates.list);
  }, []);

  const onItemClick = useCallback((itemDataObj) => {
    setItemData(itemDataObj);
    setChild(modalStates.detail);
  }, []);

  const onRegionButtonClick = useCallback(() => {
    if (!activityExists) {
      return;
    }

    dispatch(openModal(index, null as never, {
      type: REGION_NOTE,
      size: ModalSize.L,
      mode: ModalMode.EDIT_REGION,
      activity,
    }));
    dispatch(trackHeapEvent('View Field Region Note'));
  }, [index, activityExists, activity, dispatch]);

  const saveClick = useCallback(() => {
    dispatch(openModal(index, null as never, {
      type: REGION_NOTE,
      size: ModalSize.L,
      mode: ModalMode.ADD_REGION,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore It seems we are mixing activities and regions. But we are ignoring it for now give it might be a braking change.
      region: activity?.geometry,
    }));
  }, [index, activity?.geometry, dispatch]);

  const onNotePadClick = useCallback(() => {
    if (regionFormMode === ModalMode.ADD_REGION) {
      saveClick();
    } else {
      onRegionButtonClick();
    }
  }, [regionFormMode, saveClick, onRegionButtonClick]);

  const onDeleteClick = useCallback(() => {
    setDeleteConfirmationModal(false);
    dispatch(deleteActivity(activity, { index }));
  }, [activity, index, dispatch]);

  const onDeleteCancelConfirm = useCallback(() => {
    dispatch(resizeModal(ModalSize.L));
    onDeleteClick();
  }, [dispatch, onDeleteClick]);

  const onCancelConfirm = useCallback(() => {
    dispatch(closeModal(index));
    dispatch(setActivity(index, null));
  }, [dispatch, index]);

  const onCancelClick = useCallback(() => {
    if (regionFormMode === ModalMode.EDIT_REGION) {
      onCancelConfirm();
    } else {
      dispatch(resizeModal(ModalSize.S));
      setCloseConfirmationModal(true);
    }
  }, [onCancelConfirm, regionFormMode, dispatch, setCloseConfirmationModal]);

  const onCancelGoBack = useCallback(() => {
    dispatch(resizeModal(ModalSize.L));
    setCloseConfirmationModal(false);
  }, [dispatch, setCloseConfirmationModal]);

  const onDeleteConfirm = useCallback(() => {
    dispatch(resizeModal(ModalSize.S));
    setDeleteConfirmationModal(true);
  }, [setDeleteConfirmationModal, dispatch]);

  const onDeleteCancelGoBack = useCallback(() => {
    dispatch(resizeModal(ModalSize.L));
    setDeleteConfirmationModal(false);
  }, [dispatch, setDeleteConfirmationModal]);

  const showSaveButton = regionFormMode === ModalMode.ADD_REGION;

  const geoJson = activity?.id
    ? getGeoJson([fieldBoundary, activity.location])
    : getGeoJson([fieldBoundary]);

  return {
    activity,
    activityExists,
    cropCode,
    closeConfirmationModal,
    deleteConfirmationModal,
    fieldBoundary,
    fieldName,
    geoJson,
    hasRegionReportData,
    isAirSeeder,
    isDetailView: child === modalStates.detail,
    isListView: child === modalStates.list,
    isLoadingReport,
    isRegionValid,
    itemData,
    locale,
    onCancelClick,
    onCancelConfirm,
    onCancelGoBack,
    onDeleteCancelConfirm,
    onDeleteCancelGoBack,
    onDeleteConfirm,
    onItemClick,
    onNotePadClick,
    onRegionButtonClick,
    onStep2Click,
    reportData,
    saveClick,
    showSaveButton,
  };
};